import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { API_URL } from '../config';

// Registriere Chart.js-Komponenten
// ChartJS.register(ArcElement, Tooltip, Legend);

function AdminView() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showError, setShowError] = useState(false);
  const [drinks, setDrinks] = useState([]);
  const [editingDrink, setEditingDrink] = useState(null);
  const [newDrink, setNewDrink] = useState({
    name: '',
    price: '',
    description: '',
    sold_out: false,
    stock: 0,
    min_stock: 10,
    reorder_point: 20,
    unit: 'Flaschen'
  });
  const [statistics, setStatistics] = useState({
    total_orders: 0,
    total_revenue: 0,
    average_order_value: 0,
    most_popular_drink: null,
    open_orders: 0,
    drinks: []
  });
  const [settings, setSettings] = useState({
    show_order_count: true,
    show_last_order: true,
    show_order_list: true
  });
  const [error, setError] = useState(null);
  const [orderStats, setOrderStats] = useState({
    drinkStats: {},
    drinkTrends: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });
  const [showSoldOut, setShowSoldOut] = useState(true);
  const [drinkOrder, setDrinkOrder] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showStockDialog, setShowStockDialog] = useState(false);
  const [selectedDrink, setSelectedDrink] = useState(null);
  const [stockChange, setStockChange] = useState('');

  // Authentifizierung
  useEffect(() => {
    // Prüfe, ob bereits authentifiziert
    const checkAuth = async () => {
      const storedAuth = localStorage.getItem('isAuthenticated');
      if (storedAuth === 'true') {
        setIsAuthenticated(true);
      }
    };
    checkAuth();
  }, []);

  // Datenladung
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) return;

      try {
        setIsLoading(true);
        setError(null);

        // Lade Daten parallel
        const responses = await Promise.allSettled([
          axios.get(`${API_URL}/drinks`),
          axios.get(`${API_URL}/settings`),
          axios.get(`${API_URL}/statistics`),
          axios.get(`${API_URL}/drinks/inventory-alerts`)
        ]);

        // Verarbeite die Ergebnisse
        responses.forEach((response, index) => {
          if (response.status === 'fulfilled') {
            switch(index) {
              case 0:
                setDrinks(response.value.data);
                break;
              case 1:
                setSettings(response.value.data);
                break;
              case 2:
                setStatistics(response.value.data);
                break;
              case 3:
                setAlerts(response.value.data);
                break;
            }
          } else {
            console.error(`Error loading data ${index}:`, response.reason);
          }
        });

        // Wenn alle Anfragen fehlgeschlagen sind, zeige Fehler
        if (responses.every(r => r.status === 'rejected')) {
          setError('Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.');
        }

      } catch (error) {
        console.error('Error loading data:', error);
        setError(error.response?.data?.detail || 'Fehler beim Laden der Daten');
      } finally {
        setIsLoading(false);
      }
    };

    // Initial laden
    loadData();

    // Periodische Aktualisierung
    if (isAuthenticated) {
      const interval = setInterval(loadData, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError(null);

      const response = await axios.post(`${API_URL}/verify-admin`, { password });
      
      if (response.data.valid) {
        setIsAuthenticated(true);
        localStorage.setItem('isAuthenticated', 'true');
        setPassword('');
      } else {
        setError('Falsches Passwort');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.detail || 'Fehler beim Login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('isAuthenticated');
    setDrinks([]);
    setStatistics({
      total_orders: 0,
      total_revenue: 0,
      average_order_value: 0,
      most_popular_drink: null,
      open_orders: 0,
      drinks: []
    });
  };

  const fetchDrinks = async () => {
    try {
      const response = await axios.get(`${API_URL}/drinks`);
      setDrinks(response.data);
    } catch (error) {
      console.error('Error fetching drinks:', error);
      throw error;
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(drinkOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setDrinkOrder(items);
    
    try {
      await axios.put(`${API_URL}/drinks/reorder`, { order: items });
    } catch (error) {
      console.error('Error updating drink order:', error);
      setError('Fehler beim Aktualisieren der Reihenfolge');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Konvertiere price zu einer Zahl
      const drinkData = {
        name: newDrink.name,
        price: parseFloat(newDrink.price),
        description: newDrink.description,
        sold_out: newDrink.sold_out,
        stock: newDrink.stock,
        min_stock: newDrink.min_stock,
        reorder_point: newDrink.reorder_point,
        unit: newDrink.unit
      };
      
      await axios.post(`${API_URL}/drinks`, drinkData);
      setNewDrink({ 
        name: '', 
        price: '', 
        description: '', 
        sold_out: false,
        stock: 0,
        min_stock: 10,
        reorder_point: 20,
        unit: 'Flaschen'
      });
      fetchDrinks();
      setError(null);
    } catch (error) {
      setError('Fehler beim Hinzufügen des Getränks');
      console.error('Error adding drink:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/drinks/${id}`);
      fetchDrinks();
      setError(null);
    } catch (error) {
      setError('Fehler beim Löschen des Getränks');
      console.error('Error deleting drink:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDrink(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const updateSettings = async (key, value) => {
    try {
      const newSettings = { ...settings, [key]: value };
      await axios.post(`${API_URL}/settings`, newSettings);
      setSettings(newSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleEdit = (drink) => {
    setEditingDrink({
      id: drink.id,
      name: drink.name,
      price: drink.price,
      description: drink.description,
      sold_out: drink.sold_out,
      stock: drink.stock,
      min_stock: drink.min_stock,
      reorder_point: drink.reorder_point,
      unit: drink.unit
    });
  };

  const handleUpdate = async () => {
    try {
      const response = await axios.put(`${API_URL}/drinks/${editingDrink.id}`, {
        name: editingDrink.name,
        price: parseFloat(editingDrink.price),
        description: editingDrink.description,
        sold_out: editingDrink.sold_out,
        stock: editingDrink.stock,
        min_stock: editingDrink.min_stock,
        reorder_point: editingDrink.reorder_point,
        unit: editingDrink.unit
      });
      setEditingDrink(null);
      fetchDrinks();
      setError(null);
    } catch (error) {
      setError('Fehler beim Aktualisieren des Getränks');
      console.error('Error updating drink:', error);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingDrink(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleSoldOut = async (drinkId) => {
    try {
      await axios.put(`${API_URL}/drinks/${drinkId}/sold-out`);
      await fetchDrinks();
    } catch (error) {
      setError('Fehler beim Ändern des Ausverkauft-Status');
      console.error('Fehler:', error);
    }
  };

  const sortedDrinks = useMemo(() => {
    if (drinkOrder.length === 0) return drinks;
    
    return [...drinks].sort((a, b) => {
      const indexA = drinkOrder.indexOf(a.id);
      const indexB = drinkOrder.indexOf(b.id);
      return indexA - indexB;
    });
  }, [drinks, drinkOrder]);

  const filteredDrinks = useMemo(() => {
    return showSoldOut ? drinks : drinks.filter(drink => !drink.sold_out);
  }, [drinks, showSoldOut]);

  const requestSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '•';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  // Funktion zum Zurücksetzen der Statistiken
  const handleReset = async () => {
    if (window.confirm('Möchten Sie wirklich alle Statistiken und Bestellungen zurücksetzen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      try {
        await axios.post(`${API_URL}/statistics/reset`);
        await Promise.all([
          fetchDrinks(),
          fetchOrderStats()
        ]);
        setLastOrderCount(0);
      } catch (error) {
        setError('Fehler beim Zurücksetzen der Statistiken');
        console.error('Reset error:', error);
      }
    }
  };

  // Farbpalette für Getränke
  const drinkColors = {
    backgroundColor: [
      'rgba(75, 192, 192, 0.5)',
      'rgba(255, 99, 132, 0.5)',
      'rgba(54, 162, 235, 0.5)',
      'rgba(255, 206, 86, 0.5)',
      'rgba(153, 102, 255, 0.5)',
    ],
    borderColor: [
      'rgba(75, 192, 192, 1)',
      'rgba(255, 99, 132, 1)',
      'rgba(54, 162, 235, 1)',
      'rgba(255, 206, 86, 1)',
      'rgba(153, 102, 255, 1)',
    ]
  };

  // Vorbereitung der Daten für das Pie Chart
  const pieData = {
    labels: drinks.map(drink => drink.name),
    datasets: [
      {
        data: drinks.map(drink => orderStats.drinkStats[drink.name] || 0),
        backgroundColor: drinkColors.backgroundColor,
        borderColor: drinkColors.borderColor,
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#000',
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Hilfsfunktion für die Trenddarstellung
  const renderTrendBars = (data, color) => {
    const values = data || Array(10).fill(0);
    const max = Math.max(...values, 1);  // Mindestens 1, um Division durch 0 zu vermeiden
    
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        height: '100%',
        width: '100%',
        gap: 0.5
      }}>
        {values.map((value, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              backgroundColor: color,
              height: `${(value / max) * 100}%`,
              minHeight: 2,
              borderRadius: '2px',
              transition: 'height 0.3s ease'
            }}
          />
        ))}
      </Box>
    );
  };

  const handleStockChange = async () => {
    if (!selectedDrink || !stockChange) return;

    try {
      await axios.put(`${API_URL}/drinks/${selectedDrink.id}/stock`, stockChange);
      setShowStockDialog(false);
      setSelectedDrink(null);
      setStockChange('');
      fetchDrinks();
      fetchAlerts();
    } catch (error) {
      setError(error.response?.data?.detail || 'Fehler beim Aktualisieren des Bestands');
    }
  };

  const openStockDialog = (drink) => {
    setSelectedDrink(drink);
    setStockChange('');
    setShowStockDialog(true);
  };

  if (!isAuthenticated) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Admin Login
          </Typography>
          <Box component="form" onSubmit={handlePasswordSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Passwort"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Login'}
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  if (isLoading && (!drinks.length || !statistics)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          {error}
          <Button 
            sx={{ ml: 2 }} 
            onClick={() => window.location.reload()}
          >
            Neu laden
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <AppBar position="static" sx={{ mb: 4 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Admin-Bereich
          </Typography>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Grid container spacing={3}>
        {/* Statistiken */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Statistiken</Typography>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleReset}
                disabled={isLoading}
              >
                Zurücksetzen
              </Button>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    bgcolor: 'primary.light', 
                    color: 'white',
                    height: '140px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Gesamtbestellungen
                  </Typography>
                  <Typography variant="h4">
                    {statistics.total_orders}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    bgcolor: 'success.light', 
                    color: 'white',
                    height: '140px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Gesamtumsatz
                  </Typography>
                  <Typography variant="h4">
                    {statistics.total_revenue?.toFixed(2)} M$
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    bgcolor: 'info.light', 
                    color: 'white',
                    height: '140px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Offene Bestellungen
                  </Typography>
                  <Typography variant="h4">
                    {statistics.open_orders}
                  </Typography>
                  {statistics.total_orders > 0 && (
                    <Typography variant="subtitle2">
                      ({((statistics.open_orders / statistics.total_orders) * 100).toFixed(1)}%)
                    </Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    bgcolor: 'warning.light', 
                    color: 'white',
                    height: '140px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Durchschnittlicher Bestellwert
                  </Typography>
                  <Typography variant="h4">
                    {statistics.average_order_value?.toFixed(2)} M$
                  </Typography>
                </Paper>
              </Grid>
              {statistics.most_popular_drink && (
                <Grid item xs={12}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center', 
                      bgcolor: 'secondary.light', 
                      color: 'white', 
                      mt: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      minHeight: '100px'
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      Beliebtestes Getränk
                    </Typography>
                    <Typography variant="h4">
                      {statistics.most_popular_drink.name}
                    </Typography>
                    <Typography variant="subtitle2">
                      Preis: {statistics.most_popular_drink.price?.toFixed(2)} M$
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Lagerbestand Warnungen */}
        {alerts.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Lagerbestand Warnungen
              </Typography>
              {alerts.map((alert, index) => (
                <Alert 
                  key={index} 
                  severity={alert.type === 'critical' ? 'error' : alert.type === 'warning' ? 'warning' : 'info'}
                  sx={{ mb: 1 }}
                  action={
                    <Button 
                      color="inherit" 
                      size="small"
                      onClick={() => openStockDialog(drinks.find(d => d.id === alert.drink_id))}
                    >
                      Bestand anpassen
                    </Button>
                  }
                >
                  <AlertTitle>{alert.name}</AlertTitle>
                  {alert.message}
                </Alert>
              ))}
            </Paper>
          </Grid>
        )}

        {/* Getränke hinzufügen */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Getränk hinzufügen
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Name"
                    name="name"
                    value={newDrink.name}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Preis"
                    name="price"
                    type="number"
                    step="0.01"
                    value={newDrink.price}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Beschreibung"
                    name="description"
                    value={newDrink.description}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Lagerbestand"
                    name="stock"
                    type="number"
                    value={newDrink.stock}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Einheit"
                    name="unit"
                    value={newDrink.unit}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Mindestbestand"
                    name="min_stock"
                    type="number"
                    value={newDrink.min_stock}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Nachbestellpunkt"
                    name="reorder_point"
                    type="number"
                    value={newDrink.reorder_point}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newDrink.sold_out}
                        onChange={(e) => setNewDrink(prev => ({
                          ...prev,
                          sold_out: e.target.checked
                        }))}
                        color="primary"
                      />
                    }
                    label="Ausverkauft"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    fullWidth
                    size="large"
                    sx={{ mt: 2 }}
                  >
                    Hinzufügen
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Getränkeliste */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Getränke verwalten</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={showSoldOut}
                    onChange={(e) => setShowSoldOut(e.target.checked)}
                  />
                }
                label="Ausverkaufte Getränke anzeigen"
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Getränke
                </Typography>
                <List>
                  {filteredDrinks.map((drink, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={drink.name} secondary={drink.description} />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => handleEdit(drink)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(drink.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Getränkestatistik */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '400px', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Getränkestatistik
            </Typography>
            <Box sx={{ flexGrow: 1, position: 'relative' }}>
              {isLoading ? (
                <Box sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center'
                }}>
                  <Typography>Lade Daten...</Typography>
                </Box>
              ) : drinks.length > 0 && Object.keys(orderStats.drinkStats).length > 0 ? (
                <Box sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center'
                }}>
                  <Typography>Keine Daten verfügbar</Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center'
                }}>
                  <Typography>Keine Daten verfügbar</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Getränketrends */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Getränketrends
            </Typography>
            <Grid container spacing={2}>
              {isLoading ? (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography>Lade Daten...</Typography>
                  </Box>
                </Grid>
              ) : drinks.length > 0 ? (
                drinks.map((drink, index) => (
                  <Grid item xs={12} sm={6} md={4} key={drink.id}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'white',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 'bold',
                          mb: 2
                        }}
                      >
                        {drink.name}
                      </Typography>
                      <Box 
                        sx={{ 
                          flexGrow: 1,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          p: 1,
                          height: '60px',
                          bgcolor: '#fff'
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          height: '100%',
                          width: '100%',
                          gap: 0.5
                        }}>
                          <Box
                            sx={{
                              flex: 1,
                              backgroundColor: drinkColors.borderColor[index % drinkColors.borderColor.length],
                              height: '100%',
                              minHeight: 2,
                              borderRadius: '2px',
                              transition: 'height 0.3s ease'
                            }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography>Keine Daten verfügbar</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Kassenansicht Einstellungen */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Einstellungen für Kassenansicht
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.show_order_count}
                      onChange={(e) => updateSettings('show_order_count', e.target.checked)}
                    />
                  }
                  label="Anzahl offener Bestellungen anzeigen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.show_last_order}
                      onChange={(e) => updateSettings('show_last_order', e.target.checked)}
                    />
                  }
                  label="Letzte Bestellung anzeigen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.show_order_list}
                      onChange={(e) => updateSettings('show_order_list', e.target.checked)}
                    />
                  }
                  label="Liste offener Bestellungen anzeigen"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {/* Lagerbestand Dialog */}
      <Dialog open={showStockDialog} onClose={() => setShowStockDialog(false)}>
        <DialogTitle>Lagerbestand anpassen - {selectedDrink?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>
              Aktueller Bestand: {selectedDrink?.stock} {selectedDrink?.unit}
            </Typography>
            <Typography gutterBottom>
              Mindestbestand: {selectedDrink?.min_stock} {selectedDrink?.unit}
            </Typography>
            <Typography gutterBottom>
              Nachbestellpunkt: {selectedDrink?.reorder_point} {selectedDrink?.unit}
            </Typography>
            <TextField
              label="Bestandsänderung"
              type="number"
              value={stockChange}
              onChange={(e) => setStockChange(parseInt(e.target.value))}
              fullWidth
              helperText="Positive Zahl für Zugänge, negative für Abgänge"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStockDialog(false)}>Abbrechen</Button>
          <Button 
            onClick={handleStockChange} 
            variant="contained" 
            disabled={!stockChange}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminView;
